<template>
  <v-container
    id="note"
    fluid
  >
    <div
      v-if="!summary"
      class="note-switch"
    >
      <v-row>
        <div class="col-6">
          <v-switch
            v-model="showNotes"
            class="mt-0"
            label="Show Notes"
          />
        </div>
        <div class="col-6 text-right">
          <v-btn
            color="green"
            dark
            rounded
            @click="addNewNote"
          >
            <v-icon dark>
              mdi-plus
            </v-icon>
            <span class="d-none d-sm-block">New Note</span>
          </v-btn>
        </div>
      </v-row>
    </div>
    <v-row
      v-if="showNotes && !hasNotes && !summary && !loading"
    >
      <v-col>
        <div
          class="headline text-center"
          primary-title
        >
          No notes yet
        </div>
      </v-col>
    </v-row>
    <v-row
      v-if="showNotes && hasNotes"
      dense
    >
      <v-col
        v-for="(note,noteKey) in notes"
        :key="noteKey"
      >
        <v-card
          class="mx-auto note-card"
          min-width="250"
          outlined
        >
          <v-card-text>
            <v-row>
              <div class="col-6 pt-0 pb-0">
                <v-tooltip
                  v-if="note.isRenewal"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="green"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-refresh
                    </v-icon>
                  </template>
                  <span>This note was copied with the renewal</span>
                </v-tooltip>
                <span class="text--secondary">
                  {{ note.createdByName }}
                </span>
                <span class="overline">
                  <br>({{ note.createdDateDb | formatDate }})
                </span>
              </div>
              <div class="col-6 text-right pt-0 pb-0">
                <div
                  v-if="!summary"
                >
                  <v-btn
                    icon
                    @click="edit(note)"
                  >
                    <v-icon>
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    @click="remove(note)"
                  >
                    <v-icon>
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </v-row>
            <div
              class="text--primary"
            >
              <pre>{{ note.note }}</pre>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      v-if="showNotes && !summary"
    >
      <v-progress-linear
        v-show="loading || savingData"
        indeterminate
        color="green"
      />
      <v-col>
        <v-dialog
          v-model="dialog"
          width="500"
        >
          <v-card>
            <v-card-title>
              <span
                v-if="editItem.id"
                class="headline"
              >
                Edit Note
              </span>
              <span
                v-if="!editItem.id"
                class="headline"
              >
                Add New Note
              </span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col>
                    <v-textarea
                      v-model="editItem.note"
                      :error-messages="formErrors.Note"
                      label="Note"
                      auto-grow
                      filled
                      required
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-btn
                color="secondary"
                text
                @click="dialog = false"
              >
                Cancel
              </v-btn>
              <v-spacer />
              <v-btn
                color="primary"
                text
                @click="save()"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'
  import { extractFormErrors, getErrorTitle } from '@/services/ErrorHandlers'

  export default {
    name: 'Note',

    filters: {
      formatDate: (value) => {
        let formatted = ''

        if (value) {
          const date = new Date(value)
          formatted = date.toLocaleDateString(['en-US'], { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        }

        return formatted
      },
    },

    props: {
      openEnrollment: {
        type: Object,
        default: null,
      },
      section: {
        type: Object,
        default: null,
      },
      summary: {
        type: String,
        default: null,
      },
    },

    data: () => ({
      loading: false,
      savingData: false,
      errorMessage: null,
      displayError: false,
      formErrors: {},

      dialog: false,
      showNotes: true,
      editItem: { note: '' },

      notes: null,

      noteResource: new TroodyApi('oeNote/'),
    }),

    computed: {
      hasNotes () {
        return this.notes && this.notes.length
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        // this.showNotes = this.summary // Show notes by default everywhere TROOD-160
        this.formErrors = {}
        this.loadNotes()
      },

      loadNotes () {
        this.loading = true
        this.noteResource.query({ openEnrollmentId: this.openEnrollment.id, approvalSectionId: this.section.id })
          .then(data => {
            this.loading = false
            this.notes = data
            if (this.showNotes && (!this.notes || !this.notes.length)) {
              this.showNotes = false
            }
          })
          .catch((error) => {
            this.loading = false
            this.showError(error, 'Failed to load notes!')
          })
      },

      save () {
        /**
         * The pattern for the notes are different from other add / edits
         * originally we weren't going to allow "editing" a note, but only
         * deleting a note, and adding a new one.
         * However, they want to change this and allow "edits", so this is
         * a simulated edit by removing the old, before adding the new.
         */
        if (this.editItem.id) {
          this.remove(this.editItem, true)
        } else {
          this.insert()
        }
      },

      insert () {
        this.savingData = true

        const payload = {
          openEnrollmentId: this.openEnrollment.id,
          approvalSectionId: this.section.id,
          note: this.editItem.note,
        }

        this.noteResource.save(payload)
          .then((data) => {
            this.savingData = false
            if (!this.notes) {
              this.notes = []
            }
            this.notes.push(data)
            this.$emit('saved', this.notes)
            this.formErrors = {}
            this.editItem = { note: '' }
            this.dialog = false
          })
          .catch((error) => {
            this.savingData = false
            this.formErrors = extractFormErrors(error)
            if (this.formErrors) {
              this.showError(getErrorTitle(error), 'Failed to save!')
            } else {
              this.formErrors = {}
              this.showError(error, 'Failed to save!')
            }
          })
      },

      addNewNote () {
        this.showNotes = true
        this.dialog = true
        this.editItem = { note: '' }
      },

      edit (record) {
        this.dialog = true
        this.editItem = record
      },

      remove (record, isReplaceOperation = false) {
        this.savingData = true
        this.noteResource.delete(record.id)
          .then((data) => {
            this.savingData = false

            // Rebuild the list with everything except the removed item
            this.notes = this.notes.filter((item) => {
              return item.id !== record.id
            })
            this.formErrors = {}
            if (isReplaceOperation) {
              this.insert()
            }
          })
          .catch((error) => {
            this.savingData = false
            this.showError(error, 'Failed to delete!')
          })
      },

      showError (error, msg) {
        this.loading = false
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },
    },

  }
</script>

<style lang="sass">
  .note-switch
    margin-top: -30px

  .note-card
    background-color: rgba(252, 202, 3, .2) !important

  pre
    white-space: pre-wrap       /* css-3 */
    white-space: -moz-pre-wrap  /* Mozilla, since 1999 */
    white-space: -pre-wrap      /* Opera 4-6 */
    white-space: -o-pre-wrap    /* Opera 7 */
    word-wrap: break-word       /* Internet Explorer 5.5+ */
</style>
